// src/pages/ApplyAllocator.tsx: public allocator application (no auth required).
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import LocationSelect from '../components/LocationSelect';
import { C, HEARD_ABOUT, generatePassword, sendMail } from '../lib/applications';
import {
  ORGANISATION_TYPES, TOTAL_AUM_RANGES, ALTS_ALLOCATION, TICKET_SIZES,
  QUALIFICATION_BASES, ASSET_CLASS_OPTIONS,
  organisationTypeLabel, aumRangeLabel, allocationLabel, ticketSizeLabel,
  qualificationLabel, assetClassLabels,
} from '../lib/onboardingApplications';
import {
  ApplyShell, ProgressRail, SubmittedScreen, Field, Row, Section, CheckboxGroup,
  YesNo, InfoBox, ErrorBanner, card, inputStyle, labelStyle, btnPrimary, btnGhost, stepHeading,
} from '../components/ApplyShell';

const STEPS = ['Contact Details', 'Organisation', 'Investment Profile', 'Qualification', 'Review & Submit'];

export default function ApplyAllocator() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Step 1
  const [contactName, setContactName]   = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [heardAbout, setHeardAbout]     = useState('');

  // Step 2
  const [orgName, setOrgName]         = useState('');
  const [orgType, setOrgType]         = useState('');
  const [country, setCountry]         = useState('');
  const [city, setCity]               = useState('');
  const [website, setWebsite]         = useState('');
  const [description, setDescription] = useState('');

  // Step 3
  const [aumRange, setAumRange]       = useState('');
  const [allocation, setAllocation]   = useState('');
  const [assetClasses, setAssetClasses] = useState<string[]>([]);
  const [ticketSize, setTicketSize]   = useState('');

  // Step 4
  const [isProfessional, setIsProfessional] = useState(true);
  const [qualification, setQualification]   = useState('');
  const [confirmQualified, setConfirmQualified] = useState(false);

  // Step 5
  const [confirmAccurate, setConfirmAccurate] = useState(false);
  const [confirmTerms, setConfirmTerms]       = useState(false);

  const toggleAssetClass = (v: string) =>
    setAssetClasses(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);

  const step1Valid = contactName.trim() && contactEmail.trim();
  const step2Valid = orgName.trim() && orgType;
  const step4Valid = isProfessional && qualification && confirmQualified;

  const next = () => { setError(''); setStep(s => Math.min(5, s + 1)); };
  const back = () => { setError(''); setStep(s => Math.max(1, s - 1)); };

  const submit = async () => {
    setSubmitting(true); setError('');
    const email = contactEmail.trim().toLowerCase();
    try {
      const { data: signUpData, error: signUpErr } = await supabase.auth.signUp({
        email,
        password: generatePassword(),
        options: {
          data: { full_name: contactName.trim(), role: 'allocator' },
          emailRedirectTo: `${window.location.origin}/application-status?type=allocator`,
        },
      });
      if (signUpErr && !/already registered/i.test(signUpErr.message)) throw signUpErr;

      const profileId = signUpData?.user?.id
        || (await supabase.auth.getUser()).data.user?.id
        || null;
      if (!profileId) {
        throw new Error('This email already has an account. Please sign in first, then reapply.');
      }

      const { error: appErr } = await supabase.from('allocator_applications').insert({
        contact_name: contactName.trim(),
        contact_email: email,
        contact_phone: contactPhone.trim() || null,
        heard_about: heardAbout || null,
        organisation_name: orgName.trim(),
        organisation_type: orgType,
        organisation_description: description.trim() || null,
        organisation_website: website.trim() || null,
        headquarters_country: country || null,
        headquarters_city: city || null,
        total_aum_range: aumRange || null,
        alternatives_allocation_pct: allocation || null,
        asset_classes_of_interest: assetClasses,
        typical_ticket_size: ticketSize || null,
        is_professional_investor: isProfessional,
        qualification_basis: qualification || null,
        status: 'submitted',
        profile_id: profileId,
      });
      if (appErr) throw appErr;

      await sendMail('allocator_application_received', {
        contact_email: email, contact_name: contactName.trim(),
        organisation_name: orgName.trim(),
        organisation_type: organisationTypeLabel(orgType),
      });

      setSubmitted(true);
    } catch (e: any) {
      setError(e?.message || 'Could not submit your application. Please try again.');
    }
    setSubmitting(false);
  };

  const shell = (children: React.ReactNode) => (
    <ApplyShell
      title="Apply as an Allocator"
      subtitle="Free access for qualified institutional investors"
      badge="Always Free"
    >{children}</ApplyShell>
  );

  if (submitted) return shell(
    <SubmittedScreen
      email={contactEmail}
      timeline="Our team will review your application within 2 business days."
      onContinue={() => navigate('/application-status?type=allocator')}
    />
  );

  return shell(
    <div style={{ maxWidth: '720px', margin: '0 auto', padding: '28px 24px 64px' }}>
      <ProgressRail steps={STEPS} step={step} />
      {error && <ErrorBanner text={error} />}

      {/* ── Step 1 ── */}
      {step === 1 && (
        <div style={{ ...card, padding: '28px 30px' }}>
          <h2 style={stepHeading}>Contact details</h2>
          <Field label="Full name *">
            <input style={inputStyle} value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Jane Smith" />
          </Field>
          <Field label="Email address *">
            <input style={inputStyle} type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="jane@yourorganisation.com" />
          </Field>
          <Field label="Phone number (optional)">
            <input style={inputStyle} value={contactPhone} onChange={e => setContactPhone(e.target.value)} placeholder="+44 20 1234 5678" />
          </Field>
          <Field label="How did you hear about us?">
            <select style={{ ...inputStyle, cursor: 'pointer' }} value={heardAbout} onChange={e => setHeardAbout(e.target.value)}>
              <option value="">Select…</option>
              {HEARD_ABOUT.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
          </Field>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '18px' }}>
            <button style={{ ...btnPrimary, opacity: step1Valid ? 1 : 0.5 }} disabled={!step1Valid} onClick={next}>Continue</button>
          </div>
        </div>
      )}

      {/* ── Step 2 ── */}
      {step === 2 && (
        <div style={{ ...card, padding: '28px 30px' }}>
          <h2 style={stepHeading}>Organisation details</h2>
          <Field label="Organisation name *">
            <input style={inputStyle} value={orgName} onChange={e => setOrgName(e.target.value)} placeholder="Meridian Family Office" />
          </Field>
          <Field label="Organisation type *">
            <select style={{ ...inputStyle, cursor: 'pointer' }} value={orgType} onChange={e => setOrgType(e.target.value)}>
              <option value="">Select type…</option>
              {ORGANISATION_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <div style={{ marginBottom: '14px' }}>
            <LocationSelect
              country={country} city={city}
              onCountryChange={setCountry} onCityChange={setCity}
              countryLabel="Headquarters country" cityLabel="Headquarters city"
              labelStyle={labelStyle}
            />
          </div>
          <Field label="Website (optional)">
            <input style={inputStyle} value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://yourorganisation.com" />
          </Field>
          <Field label="Brief description (optional)">
            <textarea style={{ ...inputStyle, height: '88px', resize: 'vertical', lineHeight: 1.6 }}
              value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Who you are and how you invest in alternatives..." />
          </Field>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '18px' }}>
            <button style={btnGhost} onClick={back}>Back</button>
            <button style={{ ...btnPrimary, opacity: step2Valid ? 1 : 0.5 }} disabled={!step2Valid} onClick={next}>Continue</button>
          </div>
        </div>
      )}

      {/* ── Step 3 ── */}
      {step === 3 && (
        <div style={{ ...card, padding: '28px 30px' }}>
          <h2 style={stepHeading}>Investment profile</h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Field label="Total AUM">
              <select style={{ ...inputStyle, cursor: 'pointer' }} value={aumRange} onChange={e => setAumRange(e.target.value)}>
                <option value="">Select range…</option>
                {TOTAL_AUM_RANGES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Alternatives allocation">
              <select style={{ ...inputStyle, cursor: 'pointer' }} value={allocation} onChange={e => setAllocation(e.target.value)}>
                <option value="">Select…</option>
                {ALTS_ALLOCATION.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Asset classes of interest">
            <CheckboxGroup options={ASSET_CLASS_OPTIONS} selected={assetClasses} onToggle={toggleAssetClass} columns={3} />
          </Field>
          <Field label="Typical ticket size">
            <select style={{ ...inputStyle, cursor: 'pointer' }} value={ticketSize} onChange={e => setTicketSize(e.target.value)}>
              <option value="">Select…</option>
              {TICKET_SIZES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '18px' }}>
            <button style={btnGhost} onClick={back}>Back</button>
            <button style={btnPrimary} onClick={next}>Continue</button>
          </div>
        </div>
      )}

      {/* ── Step 4 ── */}
      {step === 4 && (
        <div style={{ ...card, padding: '28px 30px' }}>
          <h2 style={stepHeading}>Investor qualification</h2>
          <InfoBox>
            Altveno is available to professional and institutional investors only. Retail investors
            are not eligible for platform access.
          </InfoBox>

          <Field label="Are you a professional or institutional investor? *">
            <YesNo value={isProfessional} onChange={setIsProfessional} />
          </Field>

          {!isProfessional && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '3px', padding: '12px 15px', marginBottom: '16px', fontSize: '12.5px', color: C.danger, lineHeight: 1.6 }}>
              Platform access is limited to professional and institutional investors, so we cannot
              accept this application. Contact hello@altveno.com if you believe this is incorrect.
            </div>
          )}

          <Field label="Qualification basis *">
            <select style={{ ...inputStyle, cursor: 'pointer' }} value={qualification}
              disabled={!isProfessional} onChange={e => setQualification(e.target.value)}>
              <option value="">Select basis…</option>
              {QUALIFICATION_BASES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '12.5px', color: C.textSec, lineHeight: 1.6, marginTop: '6px' }}>
            <input type="checkbox" checked={confirmQualified} disabled={!isProfessional}
              onChange={e => setConfirmQualified(e.target.checked)} style={{ marginTop: '2px' }} />
            I confirm I am a qualified institutional investor and am applying on behalf of {orgName || 'my organisation'}
          </label>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '18px' }}>
            <button style={btnGhost} onClick={back}>Back</button>
            <button style={{ ...btnPrimary, opacity: step4Valid ? 1 : 0.5 }} disabled={!step4Valid} onClick={next}>Continue</button>
          </div>
        </div>
      )}

      {/* ── Step 5 ── */}
      {step === 5 && (
        <div style={{ ...card, padding: '28px 30px' }}>
          <h2 style={stepHeading}>Review and submit</h2>

          <Section title="Contact">
            <Row k="Name" v={contactName} />
            <Row k="Email" v={contactEmail} />
            <Row k="Phone" v={contactPhone} />
          </Section>

          <Section title="Organisation">
            <Row k="Name" v={orgName} />
            <Row k="Type" v={organisationTypeLabel(orgType)} />
            <Row k="Headquarters" v={[city, country].filter(Boolean).join(', ')} />
            <Row k="Website" v={website} />
          </Section>

          <Section title="Investment profile">
            <Row k="Total AUM" v={aumRangeLabel(aumRange)} />
            <Row k="Alternatives allocation" v={allocationLabel(allocation)} />
            <Row k="Asset classes" v={assetClassLabels(assetClasses)} />
            <Row k="Typical ticket size" v={ticketSizeLabel(ticketSize)} />
          </Section>

          <Section title="Qualification">
            <Row k="Professional investor" v={isProfessional ? 'Yes' : 'No'} />
            <Row k="Basis" v={qualificationLabel(qualification)} />
          </Section>

          <div style={{ marginTop: '22px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '12.5px', color: C.textSec, lineHeight: 1.6 }}>
              <input type="checkbox" checked={confirmAccurate} onChange={e => setConfirmAccurate(e.target.checked)} style={{ marginTop: '2px' }} />
              I confirm that all information provided is accurate and I am authorised to apply on behalf of {orgName || 'my organisation'}
            </label>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '12.5px', color: C.textSec, lineHeight: 1.6 }}>
              <input type="checkbox" checked={confirmTerms} onChange={e => setConfirmTerms(e.target.checked)} style={{ marginTop: '2px' }} />
              <span>
                I agree to the Altveno{' '}
                <a href="/terms" target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: C.gold }}>Terms of Service</a>{' '}and{' '}
                <a href="/privacy" target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: C.gold }}>Privacy Policy</a>
              </span>
            </label>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '22px' }}>
            <button style={btnGhost} onClick={back} disabled={submitting}>Back</button>
            <button
              style={{ ...btnPrimary, opacity: confirmAccurate && confirmTerms && !submitting ? 1 : 0.5 }}
              disabled={!confirmAccurate || !confirmTerms || submitting}
              onClick={submit}
            >{submitting ? 'Submitting…' : 'Submit application'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
